/**
 * Whitelist for GraphQL subscriptions toast layer; badge updates apply to ALL types.
 *
 * Strings match creation sites as-is (deliberate case mix). Do not normalize
 * before checking — `isToastNotificationType` is an exact-set membership test.
 */
export const TOAST_NOTIFICATION_TYPES: ReadonlySet<string> = new Set([
  'LIKE',
  'like',
  'COMMENT',
  'COMMENT_REPLY',
  'community_reply',
  'MENTION',
  'COMMUNITY_MENTION',
  'follow',
  'CONNECTION_REQUEST',
  'CONNECTION_ACCEPTED',
  'DM_RECEIVED',
  'SPARK_REVIEW_READY',
  'CRITIQUE_POSTED',
]);

export function isToastNotificationType(type: string): boolean {
  return TOAST_NOTIFICATION_TYPES.has(type);
}

export type ToastSurface = 'welcome' | 'app';

/**
 * Toast types kept silent inside the /welcome onboarding wizard. SPARK_REVIEW_READY opened
 * the Arty flyout on a still-processing image (#4028); CRITIQUE_POSTED is a receipt for the
 * member's own COMPLETION_CRITIQUE step. Everything else on the whitelist toasts there,
 * actorless bot likes included: they are often the only signal a mid-wizard member gets.
 */
const WELCOME_SILENT_TYPES: ReadonlySet<string> = new Set(['SPARK_REVIEW_READY', 'CRITIQUE_POSTED']);

const WELCOME_PATH = /^\/welcome(\/|$)/i;

export function resolveToastSurface(pathname: string): ToastSurface {
  if (WELCOME_PATH.test(pathname)) {
    return 'welcome';
  }
  return 'app';
}

export function shouldToast({ type, surface }: { type: string; surface: ToastSurface }): boolean {
  if (!isToastNotificationType(type)) {
    return false;
  }
  return surface === 'app' || !WELCOME_SILENT_TYPES.has(type);
}

const REPLYABLE_TYPES: ReadonlySet<string> = new Set([
  'COMMENT',
  'COMMENT_REPLY',
  'community_reply',
  'MENTION',
  'COMMUNITY_MENTION',
  'DM_RECEIVED',
]);

/**
 * The line under a wizard toast. A wizard toast is a teaser: clicking it only dismisses, so
 * the member stays in onboarding (replying from another page ends the wizard for good —
 * `/welcome` stops being reachable once they have community activity). The notification
 * waits in the bell once they finish.
 */
export function welcomeToastCta(type: string): string {
  if (REPLYABLE_TYPES.has(type)) {
    return 'Finish setup to reply';
  }
  if (type === 'CONNECTION_REQUEST') {
    return 'Finish setup to respond';
  }
  return 'Finish setup to see it';
}

/**
 * The one toast shown when a member arrives in the wizard with unread notifications waiting
 * (e.g. back from an email the next day): live toasts only cover what lands mid-session.
 * Null when nothing qualifies; `first` is shown as-is when it is the only one.
 */
export function welcomeRecap<T extends { type: string; isRead: boolean }>(
  notifications: readonly T[]
): { count: number; first: T } | null {
  const waiting = notifications.filter((n) => !n.isRead && shouldToast({ type: n.type, surface: 'welcome' }));
  const [first] = waiting;
  if (!first) {
    return null;
  }
  return { count: waiting.length, first };
}
